/**
 * WebOs Shell - Système de Manuels
 *
 * Gère les pages de manuel (man pages) pour les commandes.
 * Les manuels sont stockés dans /usr/share/man/<command>.txt
 * Les applications externes peuvent ajouter leurs propres manuels.
 */

import { createLogger } from '../utils/debug.js';

const log = createLogger('ManPages');

// Chemin des manuels
const MAN_PATH = '/usr/share/man';

// Manuels built-in (intégrés directement pour éviter les I/O au démarrage)
const BUILTIN_MANUALS = {
  ls: `LS(1)                        Manuel WebOs                        LS(1)

NOM
       ls - liste le contenu d'un répertoire

SYNOPSIS
       ls [OPTIONS] [CHEMIN]

DESCRIPTION
       Liste les fichiers et répertoires du chemin spécifié.
       Si aucun chemin n'est donné, liste le répertoire courant.

OPTIONS
       -l     Format long avec détails (type, taille, date)
       -a     Affiche les fichiers cachés (commençant par .)
       -h     Tailles en format lisible (Ko, Mo, Go)

EXEMPLES
       ls              Liste le répertoire courant
       ls -la          Liste tous les fichiers avec détails
       ls /home        Liste le contenu de /home
       ls -lh /var     Liste /var avec tailles lisibles

VOIR AUSSI
       cd(1), pwd(1), mkdir(1)`,

  cd: `CD(1)                        Manuel WebOs                        CD(1)

NOM
       cd - change le répertoire courant

SYNOPSIS
       cd [CHEMIN]

DESCRIPTION
       Change le répertoire de travail courant.

ARGUMENTS
       CHEMIN    Répertoire de destination
       ~         Répertoire home (/home/user)
       ..        Répertoire parent
       -         Répertoire précédent

EXEMPLES
       cd /home/user    Va dans /home/user
       cd ~             Va dans le home
       cd ..            Remonte d'un niveau
       cd -             Retourne au répertoire précédent

VOIR AUSSI
       pwd(1), ls(1)`,

  pwd: `PWD(1)                       Manuel WebOs                       PWD(1)

NOM
       pwd - affiche le répertoire de travail courant

SYNOPSIS
       pwd

DESCRIPTION
       Affiche le chemin absolu du répertoire de travail courant.

EXEMPLES
       pwd              Affiche /home/user (par exemple)

VOIR AUSSI
       cd(1), ls(1)`,

  cat: `CAT(1)                       Manuel WebOs                       CAT(1)

NOM
       cat - affiche le contenu d'un fichier

SYNOPSIS
       cat FICHIER [FICHIER...]

DESCRIPTION
       Concatène et affiche le contenu des fichiers spécifiés.

EXEMPLES
       cat fichier.txt           Affiche fichier.txt
       cat f1.txt f2.txt         Affiche f1.txt puis f2.txt

VOIR AUSSI
       head(1), tail(1), less(1)`,

  mkdir: `MKDIR(1)                     Manuel WebOs                     MKDIR(1)

NOM
       mkdir - crée des répertoires

SYNOPSIS
       mkdir [OPTIONS] REPERTOIRE...

DESCRIPTION
       Crée les répertoires spécifiés.

OPTIONS
       -p     Crée les répertoires parents si nécessaire
              (ne génère pas d'erreur si le répertoire existe)

EXEMPLES
       mkdir test             Crée le répertoire 'test'
       mkdir -p a/b/c         Crée a, a/b et a/b/c

VOIR AUSSI
       rm(1), ls(1)`,

  rm: `RM(1)                        Manuel WebOs                        RM(1)

NOM
       rm - supprime des fichiers ou répertoires

SYNOPSIS
       rm [OPTIONS] FICHIER...

DESCRIPTION
       Supprime les fichiers ou répertoires spécifiés.
       ATTENTION: La suppression est définitive!

OPTIONS
       -r     Suppression récursive (pour les répertoires)
       -f     Force (ignore les erreurs, pas de confirmation)

EXEMPLES
       rm fichier.txt         Supprime fichier.txt
       rm -r dossier          Supprime dossier et son contenu
       rm -rf temp            Supprime temp sans confirmation

VOIR AUSSI
       mkdir(1), touch(1)`,

  touch: `TOUCH(1)                     Manuel WebOs                     TOUCH(1)

NOM
       touch - crée un fichier vide ou met à jour son timestamp

SYNOPSIS
       touch FICHIER...

DESCRIPTION
       Crée les fichiers spécifiés s'ils n'existent pas.
       Met à jour la date de modification s'ils existent.

EXEMPLES
       touch nouveau.txt      Crée nouveau.txt (vide)
       touch existant.txt     Met à jour la date de existant.txt

VOIR AUSSI
       rm(1), cat(1)`,

  cp: `CP(1)                        Manuel WebOs                        CP(1)

NOM
       cp - copie des fichiers

SYNOPSIS
       cp [OPTIONS] SOURCE DESTINATION

DESCRIPTION
       Copie SOURCE vers DESTINATION.

OPTIONS
       -r     Copie récursive (pour les répertoires)

EXEMPLES
       cp a.txt b.txt         Copie a.txt vers b.txt
       cp -r dir1 dir2        Copie dir1 vers dir2

VOIR AUSSI
       mv(1), rm(1)`,

  mv: `MV(1)                        Manuel WebOs                        MV(1)

NOM
       mv - déplace ou renomme des fichiers

SYNOPSIS
       mv SOURCE DESTINATION

DESCRIPTION
       Déplace SOURCE vers DESTINATION.
       Peut être utilisé pour renommer des fichiers.

EXEMPLES
       mv ancien.txt nouveau.txt    Renomme ancien.txt
       mv fichier.txt /tmp/         Déplace vers /tmp

VOIR AUSSI
       cp(1), rm(1)`,

  echo: `ECHO(1)                      Manuel WebOs                      ECHO(1)

NOM
       echo - affiche du texte

SYNOPSIS
       echo [TEXTE...]

DESCRIPTION
       Affiche les arguments séparés par des espaces, suivi
       d'un retour à la ligne.

EXEMPLES
       echo Hello World        Affiche "Hello World"
       echo $HOME              Affiche la valeur de HOME

VOIR AUSSI
       env(1), export(1)`,

  env: `ENV(1)                       Manuel WebOs                       ENV(1)

NOM
       env - affiche les variables d'environnement

SYNOPSIS
       env

DESCRIPTION
       Affiche toutes les variables d'environnement définies
       dans le shell courant.

EXEMPLES
       env                    Affiche toutes les variables

VOIR AUSSI
       export(1), echo(1)`,

  export: `EXPORT(1)                    Manuel WebOs                    EXPORT(1)

NOM
       export - définit une variable d'environnement

SYNOPSIS
       export NOM=VALEUR

DESCRIPTION
       Définit ou modifie une variable d'environnement.

EXEMPLES
       export EDITOR=nano     Définit EDITOR à "nano"
       export PATH=/bin       Définit PATH

VOIR AUSSI
       env(1), echo(1)`,

  clear: `CLEAR(1)                     Manuel WebOs                     CLEAR(1)

NOM
       clear - efface l'écran du terminal

SYNOPSIS
       clear

DESCRIPTION
       Efface tout le contenu visible du terminal et place
       le curseur en haut à gauche.

VOIR AUSSI
       history(1)`,

  history: `HISTORY(1)                   Manuel WebOs                   HISTORY(1)

NOM
       history - affiche l'historique des commandes

SYNOPSIS
       history

DESCRIPTION
       Affiche la liste des commandes précédemment exécutées
       dans cette session.

       Utilisez les flèches haut/bas pour naviguer dans
       l'historique.

VOIR AUSSI
       clear(1)`,

  head: `HEAD(1)                      Manuel WebOs                      HEAD(1)

NOM
       head - affiche le début d'un fichier

SYNOPSIS
       head [OPTIONS] FICHIER

DESCRIPTION
       Affiche les premières lignes d'un fichier.
       Par défaut, affiche les 10 premières lignes.

OPTIONS
       -n N   Affiche les N premières lignes

EXEMPLES
       head fichier.txt        Affiche 10 premières lignes
       head -n 5 fichier.txt   Affiche 5 premières lignes

VOIR AUSSI
       tail(1), cat(1)`,

  tail: `TAIL(1)                      Manuel WebOs                      TAIL(1)

NOM
       tail - affiche la fin d'un fichier

SYNOPSIS
       tail [OPTIONS] FICHIER

DESCRIPTION
       Affiche les dernières lignes d'un fichier.
       Par défaut, affiche les 10 dernières lignes.

OPTIONS
       -n N   Affiche les N dernières lignes

EXEMPLES
       tail fichier.txt        Affiche 10 dernières lignes
       tail -n 20 log.txt      Affiche 20 dernières lignes

VOIR AUSSI
       head(1), cat(1)`,

  wc: `WC(1)                        Manuel WebOs                        WC(1)

NOM
       wc - compte les lignes, mots et caractères

SYNOPSIS
       wc [OPTIONS] FICHIER

DESCRIPTION
       Affiche le nombre de lignes, mots et caractères d'un fichier.

OPTIONS
       -l     Compte uniquement les lignes
       -w     Compte uniquement les mots
       -c     Compte uniquement les caractères

EXEMPLES
       wc fichier.txt          Affiche lignes, mots, caractères
       wc -l fichier.txt       Affiche uniquement les lignes

VOIR AUSSI
       cat(1), head(1)`,

  grep: `GREP(1)                      Manuel WebOs                      GREP(1)

NOM
       grep - recherche un motif dans un fichier

SYNOPSIS
       grep [OPTIONS] MOTIF FICHIER

DESCRIPTION
       Recherche les lignes contenant MOTIF dans FICHIER
       et les affiche.

OPTIONS
       -i     Recherche insensible à la casse

EXEMPLES
       grep error log.txt      Cherche "error" dans log.txt
       grep -i TODO code.js    Cherche "todo" (insensible)

VOIR AUSSI
       cat(1), wc(1)`,

  man: `MAN(1)                       Manuel WebOs                       MAN(1)

NOM
       man - affiche le manuel d'une commande

SYNOPSIS
       man COMMANDE

DESCRIPTION
       Affiche la page de manuel de la commande spécifiée.

       Les manuels sont stockés dans /usr/share/man/.
       Les applications peuvent ajouter leurs propres manuels.

EXEMPLES
       man ls              Affiche le manuel de ls
       man man             Affiche ce manuel

VOIR AUSSI
       help(1)`,

  help: `HELP(1)                      Manuel WebOs                      HELP(1)

NOM
       help - affiche l'aide du shell

SYNOPSIS
       help

DESCRIPTION
       Affiche la liste des commandes disponibles avec
       une brève description.

       Pour plus de détails sur une commande, utilisez:
           man <commande>

VOIR AUSSI
       man(1)`,

  exit: `EXIT(1)                      Manuel WebOs                      EXIT(1)

NOM
       exit - quitte le shell

SYNOPSIS
       exit [CODE]

DESCRIPTION
       Termine la session shell.
       Un code de sortie optionnel peut être spécifié.

EXEMPLES
       exit            Quitte avec code 0
       exit 1          Quitte avec code 1

VOIR AUSSI
       help(1)`,

  // ==========================================
  // COMMANDES SYSTÈME
  // ==========================================

  hardreset: `HARDRESET(1)                 Manuel WebOs                 HARDRESET(1)

NOM
       hardreset - réinitialise WebOs aux paramètres d'usine

SYNOPSIS
       hardreset [--yes]

DESCRIPTION
       Effectue une réinitialisation complète de WebOs, supprimant :
       - Tous les fichiers utilisateur
       - Tous les comptes utilisateur
       - Toutes les applications installées
       - Toutes les configurations

       Le système sera remis dans l'état du premier lancement.

       ATTENTION: Cette action est IRRÉVERSIBLE !

OPTIONS
       --yes, -y, --confirm
              Confirme la réinitialisation sans demander de confirmation.
              Sans cette option, la commande affiche un avertissement
              et demande une confirmation explicite.

EXEMPLES
       hardreset           Affiche l'avertissement et les instructions
       hardreset --yes     Effectue immédiatement la réinitialisation

NOTES
       Après la réinitialisation, la page sera automatiquement rechargée
       et l'assistant de premier démarrage sera relancé.

VOIR AUSSI
       exit(1)`,

  // ==========================================
  // COMMANDES UTILISATEUR
  // ==========================================

  whoami: `WHOAMI(1)                    Manuel WebOs                    WHOAMI(1)

NOM
       whoami - affiche l'utilisateur courant

SYNOPSIS
       whoami

DESCRIPTION
       Affiche le nom de l'utilisateur actuellement connecté.

EXEMPLES
       whoami          Affiche "admin" ou "user"

VOIR AUSSI
       users(1), su(1)`,

  users: `USERS(1)                     Manuel WebOs                     USERS(1)

NOM
       users - liste les utilisateurs du système

SYNOPSIS
       users

DESCRIPTION
       Affiche la liste de tous les utilisateurs enregistrés
       dans le système avec leurs informations.

       L'utilisateur courant est marqué d'un *.
       Les administrateurs (sudo) sont marqués en jaune.

EXEMPLES
       users           Liste tous les utilisateurs

VOIR AUSSI
       whoami(1), useradd(1), userdel(1)`,

  passwd: `PASSWD(1)                    Manuel WebOs                    PASSWD(1)

NOM
       passwd - change le mot de passe d'un utilisateur

SYNOPSIS
       passwd [UTILISATEUR]

DESCRIPTION
       Change le mot de passe de l'utilisateur spécifié.
       Sans argument, change le mot de passe de l'utilisateur courant.

       Pour changer le mot de passe d'un autre utilisateur,
       les droits administrateur sont requis.

EXEMPLES
       passwd              Change son propre mot de passe
       passwd john         Change le mot de passe de john (admin)

VOIR AUSSI
       whoami(1), su(1)`,

  su: `SU(1)                        Manuel WebOs                        SU(1)

NOM
       su - change d'utilisateur

SYNOPSIS
       su UTILISATEUR

DESCRIPTION
       Ouvre une session en tant qu'un autre utilisateur.
       Le mot de passe de l'utilisateur cible est demandé.

       Cette commande change l'environnement du shell courant
       (USER, HOME, PATH, etc.).

EXEMPLES
       su admin        Se connecte en tant qu'admin
       su john         Se connecte en tant que john

VOIR AUSSI
       whoami(1), sudo(1), passwd(1)`,

  sudo: `SUDO(1)                      Manuel WebOs                      SUDO(1)

NOM
       sudo - exécute une commande en tant qu'administrateur

SYNOPSIS
       sudo COMMANDE [ARGUMENTS...]

DESCRIPTION
       Exécute une commande avec les privilèges administrateur.

       Seuls les utilisateurs listés dans /etc/sudoers peuvent
       utiliser cette commande.

       Le mot de passe de l'utilisateur courant est demandé.
       Une session sudo de 5 minutes est créée après validation.

EXEMPLES
       sudo useradd bob     Crée l'utilisateur bob
       sudo rm -r /tmp/*    Supprime avec droits admin

VOIR AUSSI
       su(1), useradd(1), userdel(1)`,

  useradd: `USERADD(1)                   Manuel WebOs                   USERADD(1)

NOM
       useradd - crée un nouvel utilisateur

SYNOPSIS
       useradd [OPTIONS] NOM_UTILISATEUR

DESCRIPTION
       Crée un nouvel utilisateur dans le système.
       Nécessite les droits administrateur (sudo).

       Un répertoire home est automatiquement créé dans
       /home/<nom_utilisateur>/.

OPTIONS
       -s     Donne les droits sudo (administrateur)

EXEMPLES
       useradd bob         Crée l'utilisateur bob
       useradd -s alice    Crée alice avec droits admin

VOIR AUSSI
       userdel(1), users(1), passwd(1)`,

  userdel: `USERDEL(1)                   Manuel WebOs                   USERDEL(1)

NOM
       userdel - supprime un utilisateur

SYNOPSIS
       userdel [OPTIONS] NOM_UTILISATEUR

DESCRIPTION
       Supprime un utilisateur du système.
       Nécessite les droits administrateur (sudo).

       Par défaut, le répertoire home est conservé.
       Le dernier administrateur ne peut pas être supprimé.

OPTIONS
       -r     Supprime également le répertoire home

EXEMPLES
       userdel bob         Supprime bob (conserve /home/bob)
       userdel -r alice    Supprime alice et son home

VOIR AUSSI
       useradd(1), users(1)`,

  // ==========================================
  // GESTIONNAIRE DE PAQUETS
  // ==========================================

  web: `WEB(1)                       Manuel WebOs                       WEB(1)

NOM
       web - gestionnaire de paquets WebOs

SYNOPSIS
       web <commande> [arguments]

DESCRIPTION
       Gestionnaire de paquets pour installer, désinstaller et gérer
       les applications WebOs depuis le registre officiel.

COMMANDES
       install <paquet>
              Télécharge et installe un paquet depuis le registre.
              Les fichiers sont installés dans /usr/bin/<paquet>/.

       uninstall <paquet>
              Désinstalle un paquet et supprime ses fichiers.
              Alias: remove

       list
              Affiche la liste des paquets installés avec leur version
              et type (tui, gui, daemon).

       search [requête]
              Recherche des paquets dans le registre.
              Sans argument, liste tous les paquets disponibles.

       info <paquet>
              Affiche les informations détaillées d'un paquet:
              description, version, auteur, type, taille, etc.

       update
              Met à jour le registre des paquets (vérifie la connexion).

TYPES DE PAQUETS
       tui    Application en mode texte (terminal)
       gui    Application graphique (fenêtre)
       daemon Service en arrière-plan

EXEMPLES
       web install clock       Installe l'horloge
       web uninstall clock     Désinstalle l'horloge
       web list                Liste les paquets installés
       web search              Affiche tous les paquets
       web search calc         Recherche "calc"
       web info calculator     Infos sur calculator

FICHIERS
       /var/lib/webpkg/installed.json
              Liste des paquets installés

       /packages/registry.json
              Registre des paquets disponibles (serveur)

VOIR AUSSI
       help(1)`
};

// Chemin du registre des commandes (pour help)
const HELP_REGISTRY_PATH = '/usr/share/help-registry.json';

/**
 * Classe de gestion des manuels
 */
export class ManPages {
  constructor(fs) {
    this.fs = fs;
    this.cache = new Map();

    // Registre des commandes pour help (catégorie -> [{name, description}])
    this.helpRegistry = new Map();

    log.debug('ManPages initialized');
  }

  /**
   * Initialise le système de manuels (crée le répertoire si nécessaire)
   */
  async init() {
    const exists = await this.fs.exists(MAN_PATH);
    if (!exists) {
      await this.fs.mkdir(MAN_PATH, { recursive: true });
      log.debug('Created man directory:', MAN_PATH);
    }

    // Pré-charger les manuels built-in dans le cache
    for (const [name, content] of Object.entries(BUILTIN_MANUALS)) {
      this.cache.set(name, content);
    }

    // Charger le registre help depuis le FS (apps externes)
    await this.loadHelpRegistry();

    log.info(`ManPages ready (${Object.keys(BUILTIN_MANUALS).length} built-in)`);
  }

  /**
   * Charge le registre help depuis le FS
   */
  async loadHelpRegistry() {
    try {
      if (await this.fs.exists(HELP_REGISTRY_PATH)) {
        const content = await this.fs.readFile(HELP_REGISTRY_PATH);
        const data = JSON.parse(content);

        for (const [category, commands] of Object.entries(data)) {
          this.helpRegistry.set(category, commands);
        }

        log.debug('Help registry loaded');
      }
    } catch (err) {
      log.warn('Error loading help registry:', err.message);
    }
  }

  /**
   * Sauvegarde le registre help dans le FS
   */
  async saveHelpRegistry() {
    try {
      const data = {};
      for (const [category, commands] of this.helpRegistry) {
        data[category] = commands;
      }
      await this.fs.writeFile(HELP_REGISTRY_PATH, JSON.stringify(data, null, 2));
      log.debug('Help registry saved');
    } catch (err) {
      log.warn('Error saving help registry:', err.message);
    }
  }

  /**
   * Récupère le manuel d'une commande
   * @param {string} command - Nom de la commande
   * @returns {Promise<string|null>} - Contenu du manuel ou null
   */
  async getManual(command) {
    // Vérifier le cache d'abord
    if (this.cache.has(command)) {
      return this.cache.get(command);
    }

    // Essayer de charger depuis le FS
    const manFile = `${MAN_PATH}/${command}.txt`;

    try {
      if (await this.fs.exists(manFile)) {
        const content = await this.fs.readFile(manFile);
        this.cache.set(command, content);
        log.debug('Loaded manual from FS:', command);
        return content;
      }
    } catch (err) {
      log.warn('Error loading manual:', command, err.message);
    }

    return null;
  }

  /**
   * Installe un manuel (utilisé par les applications externes)
   * @param {string} command - Nom de la commande
   * @param {string} content - Contenu du manuel
   */
  async installManual(command, content) {
    const manFile = `${MAN_PATH}/${command}.txt`;

    try {
      await this.fs.writeFile(manFile, content);
      this.cache.set(command, content);
      log.info('Manual installed:', command);
    } catch (err) {
      log.error('Failed to install manual:', command, err.message);
      throw err;
    }
  }

  /**
   * Liste tous les manuels disponibles
   * @returns {Promise<string[]>}
   */
  async listManuals() {
    const manuals = new Set(this.cache.keys());

    // Ajouter les manuels du FS
    try {
      const entries = await this.fs.readdir(MAN_PATH);
      for (const entry of entries) {
        if (entry.name.endsWith('.txt')) {
          manuals.add(entry.name.replace('.txt', ''));
        }
      }
    } catch (err) {
      log.warn('Error listing manuals:', err.message);
    }

    return Array.from(manuals).sort();
  }

  /**
   * Vérifie si un manuel existe
   * @param {string} command
   * @returns {Promise<boolean>}
   */
  async hasManual(command) {
    if (this.cache.has(command)) {
      return true;
    }

    const manFile = `${MAN_PATH}/${command}.txt`;
    return await this.fs.exists(manFile);
  }

  /**
   * Enregistre une commande externe dans le système help
   * Appelé par les apps lors de leur installation
   * @param {string} command - Nom de la commande
   * @param {string} description - Description courte pour help
   * @param {string} category - Catégorie (ex: "Éditeurs", "Outils")
   * @param {string} manualContent - Contenu du manuel (optionnel)
   */
  async registerCommand(command, description, category = 'Applications', manualContent = null) {
    // Ajouter au registre help
    if (!this.helpRegistry.has(category)) {
      this.helpRegistry.set(category, []);
    }

    const commands = this.helpRegistry.get(category);

    // Éviter les doublons
    const existing = commands.findIndex(c => c.name === command);
    if (existing >= 0) {
      commands[existing] = { name: command, description };
    } else {
      commands.push({ name: command, description });
    }

    // Trier par nom
    commands.sort((a, b) => a.name.localeCompare(b.name));

    // Sauvegarder
    await this.saveHelpRegistry();

    // Installer le manuel si fourni
    if (manualContent) {
      await this.installManual(command, manualContent);
    }

    log.info(`Command registered: ${command} in ${category}`);
  }

  /**
   * Désenregistre une commande externe
   * Appelé lors de la désinstallation d'une app
   * @param {string} command - Nom de la commande
   */
  async unregisterCommand(command) {
    // Retirer du registre help
    for (const [category, commands] of this.helpRegistry) {
      const index = commands.findIndex(c => c.name === command);
      if (index >= 0) {
        commands.splice(index, 1);

        // Supprimer la catégorie si vide
        if (commands.length === 0) {
          this.helpRegistry.delete(category);
        }

        await this.saveHelpRegistry();
        log.info(`Command unregistered: ${command}`);
        break;
      }
    }

    // Supprimer le manuel
    const manFile = `${MAN_PATH}/${command}.txt`;
    try {
      if (await this.fs.exists(manFile)) {
        await this.fs.delete(manFile);
      }
      this.cache.delete(command);
    } catch (err) {
      log.warn('Error removing manual:', err.message);
    }
  }

  /**
   * Retourne le registre help pour les commandes externes
   * @returns {Map<string, Array<{name: string, description: string}>>}
   */
  getHelpRegistry() {
    return this.helpRegistry;
  }

  /**
   * Retourne toutes les catégories du registre help
   * @returns {string[]}
   */
  getHelpCategories() {
    return Array.from(this.helpRegistry.keys()).sort();
  }
}

export default ManPages;
